import { useEffect, useState } from 'react';
import { Alert, Button, Group, Text, Loader } from '@mantine/core';
import { IconMailExclamation, IconCheck, IconX } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import {
  isEmailConfirmedLocally,
  markEmailConfirmedLocally,
} from '@/utils/emailConfirmationMarker';

/**
 * Slim banner shown to authenticated users whose <c>emailConfirmed</c> flag is
 * false. One-click "resend confirmation" calls the existing
 * <c>POST /api/auth/resend-confirmation</c> endpoint. Silently dismisses for
 * the rest of the session if the user hits "Dismiss" — but reappears on the
 * next login since the flag is server-driven.
 */
export function EmailConfirmationBanner() {
  const { user, isAuthenticated } = useEnrichedAuth();
  const authService = useAuthService();

  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = (user as { email?: string | null } | null)?.email ?? '';

  // Cast through unknown so TS doesn't fight the (intentionally) loose user shape.
  const flag = (user as { emailConfirmed?: boolean } | null)?.emailConfirmed;

  // Watch the local "confirmed" marker so the banner disappears the moment
  // the viewer hits the confirmation link in another tab. The custom event
  // fires from the same tab; the storage event fires for cross-tab updates.
  const [locallyConfirmed, setLocallyConfirmed] = useState(() => isEmailConfirmedLocally(email));
  useEffect(() => {
    setLocallyConfirmed(isEmailConfirmedLocally(email));
    const refresh = () => setLocallyConfirmed(isEmailConfirmedLocally(email));
    window.addEventListener('storage', refresh);
    window.addEventListener('ccms:email-confirmed', refresh as EventListener);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('ccms:email-confirmed', refresh as EventListener);
    };
  }, [email]);

  // Authoritative check — ask the backend whether the email is confirmed.
  // Runs on mount, on email change, and when the window regains focus (so
  // the banner disappears within seconds of the viewer confirming in
  // another tab). Avoids the "stale JWT" trap: the cached JWT was minted
  // pre-confirmation, but the backend's User.EmailConfirmed flag is the
  // truth.
  useEffect(() => {
    if (!isAuthenticated || !email || flag !== false || locallyConfirmed) return;
    let cancelled = false;
    let timer: number | null = null;

    const check = async () => {
      try {
        const ok = await authService.getEmailConfirmationStatus(email);
        if (cancelled) return;
        if (ok) {
          markEmailConfirmedLocally(email); // also fans out to other tabs
          setLocallyConfirmed(true);
        }
      } catch {
        /* network blip — try again on next tick */
      }
    };

    // Initial check, then on every focus regain.
    check();
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    // Light polling as a safety net (every 30 s) — cheap endpoint, no payload.
    timer = window.setInterval(check, 30_000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      if (timer != null) window.clearInterval(timer);
    };
  }, [isAuthenticated, email, flag, locallyConfirmed, authService]);

  // Default to true if missing — keeps the banner hidden for older sessions
  // that pre-date the field rather than falsely accusing every user.
  const needsConfirmation = isAuthenticated && flag === false && !locallyConfirmed;

  if (!needsConfirmation || dismissed) return null;

  const resend = async () => {
    if (!email) return;
    setBusy(true);
    setError(null);
    try {
      await authService.resendConfirmation({ email, appUrl: window.location.origin });
      setSentOk(true);
      window.setTimeout(() => setSentOk(false), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send confirmation email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Alert
      variant="light"
      color={sentOk ? 'teal' : 'yellow'}
      radius={0}
      icon={sentOk ? <IconCheck size={18} /> : <IconMailExclamation size={18} />}
      withCloseButton
      onClose={() => setDismissed(true)}
      closeButtonLabel="Dismiss until next sign-in"
      styles={{
        root: {
          borderRadius: 0,
          borderLeft: 0,
          borderRight: 0,
        },
        body: { paddingTop: 6, paddingBottom: 6 },
        message: { fontSize: 14 },
      }}
    >
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Text size="sm">
          {sentOk
            ? <>We've just resent the confirmation link to <b>{email}</b>. Check your inbox.</>
            : error
              ? <>Couldn't resend the confirmation email — {error}.</>
              : <>Please confirm your email address ({<b>{email}</b>}) so we can keep your account secure.</>
          }
        </Text>
        {!sentOk && (
          <Button
            size="compact-sm"
            variant="filled"
            color={error ? 'red' : 'yellow'}
            leftSection={busy ? <Loader size={12} /> : error ? <IconX size={14} /> : <IconMailExclamation size={14} />}
            onClick={resend}
            disabled={busy || !email}
          >
            {busy ? 'Sending…' : error ? 'Try again' : 'Resend confirmation'}
          </Button>
        )}
      </Group>
    </Alert>
  );
}
